import { z } from 'zod';
import { canonicalHash, layerOutlineMm, polygonArea, type CanonicalDocument } from '@besbello/design-core';
import { countInksForPricing } from '../validate/inks.js';
import { selectPriceRule, type PriceBook, type PriceRuleVersion, type Surcharge } from './pricebook.js';
import type { TechniqueId } from '../rules.js';

/**
 * MOTOR DE PRECIO — puro, determinista y explicado.
 *
 * "Explicado" no es un adorno: cada linea del desglose dice QUE regla la genero
 * y con que numeros. Un comercial tiene que poder defender el precio por
 * telefono sin abrir el codigo (criterio de aceptacion Fase 3.3).
 */

export const OrderLine = z.object({
  variantId: z.string().min(1),
  sku: z.string().min(1),
  qty: z.number().int().positive(),
  /** Tarifa base de la prenda, en centimos por unidad (viene del catalogo). */
  basePriceCents: z.number().int().nonnegative(),
});
export type OrderLine = z.infer<typeof OrderLine>;

export interface PricingInput {
  document: CanonicalDocument;
  lines: OrderLine[];
  techniqueId: TechniqueId;
  zoneId: string;
  /** Pasadas del proceso (serigrafia con subbase = 2). */
  passes: number;
  /** Prenda oscura: activa recargos declarados como `darkGarment`. */
  darkGarment: boolean;
  priceBook: PriceBook;
  /** Instante de calculo, ISO-8601. El motor NO lee el reloj. */
  now: string;
}

export interface BreakdownLine {
  /** Codigo estable del concepto; la UI lo traduce. */
  code:
    | 'PRODUCT_BASE'
    | 'SETUP'
    | 'SETUP_PER_INK'
    | 'UNIT_MARKING'
    | 'PER_INK_UNIT'
    | 'PER_PASS_UNIT'
    | 'SURCHARGE'
    | 'MINIMUM_ORDER_ADJUSTMENT';
  ruleId: string;
  ruleVersion: string;
  /** Unidades a las que se aplica el importe unitario. */
  qty: number;
  unitCents: number;
  amountCents: number;
  /** Datos con los que la UI compone la explicacion en el idioma del usuario. */
  params?: Record<string, string | number | boolean>;
}

export interface Quote {
  breakdown: BreakdownLine[];
  subtotalCents: number;
  vatCents: number;
  totalCents: number;
  totalQty: number;
  inkCount: number;
  markedAreaMm2: number;
  /** Versiones de regla aplicadas: `<id>@<version>`. */
  ruleVersions: string[];
  /** Identifica el calculo completo. Confirmar un pedido con otro hash es otro precio. */
  calculationHash: string;
  /** ISO-8601. Pasada esta fecha el presupuesto se recalcula y se avisa. */
  validUntil: string;
}

function markedAreaMm2(doc: CanonicalDocument): number {
  let area = 0;
  for (const layer of doc.layers) {
    const outline = layerOutlineMm(layer);
    if (outline.points.length >= 3) area += polygonArea(outline.points);
  }
  return Number(area.toFixed(3));
}

function surchargeApplies(s: Surcharge, ctx: { areaMm2: number; inks: number; darkGarment: boolean; technicalWhite: boolean }): boolean {
  const c = s.appliesWhen;
  if (c.areaMm2Over !== undefined && ctx.areaMm2 <= c.areaMm2Over) return false;
  if (c.inksOver !== undefined && ctx.inks <= c.inksOver) return false;
  if (c.requiresTechnicalWhite === true && !ctx.technicalWhite) return false;
  if (c.darkGarment === true && !ctx.darkGarment) return false;
  return true;
}

function hasTechnicalWhite(doc: CanonicalDocument): boolean {
  return doc.layers.some((l) => {
    if (l.kind === 'text') return l.color.type === 'technical_white';
    if (l.kind === 'shape') return l.fill?.type === 'technical_white' || l.stroke?.type === 'technical_white';
    return false;
  });
}

export function computeQuote(input: PricingInput): Quote {
  const totalQty = input.lines.reduce((acc, l) => acc + l.qty, 0);
  if (totalQty === 0) throw new Error('el presupuesto necesita al menos una unidad');

  const rule: PriceRuleVersion = selectPriceRule(
    input.priceBook,
    input.techniqueId,
    input.zoneId,
    totalQty,
    input.now,
  );

  const inkCount = countInksForPricing(input.document);
  const areaMm2 = markedAreaMm2(input.document);
  const technicalWhite = hasTechnicalWhite(input.document);
  const breakdown: BreakdownLine[] = [];
  const ruleRef = { ruleId: rule.id, ruleVersion: rule.version };

  // 1. Prenda, linea a linea: cada talla puede tener tarifa base distinta.
  for (const line of input.lines) {
    breakdown.push({
      code: 'PRODUCT_BASE',
      ...ruleRef,
      qty: line.qty,
      unitCents: line.basePriceCents,
      amountCents: line.basePriceCents * line.qty,
      params: { sku: line.sku, variantId: line.variantId },
    });
  }

  // 2. Preparacion: fija, y por tinta (cada tinta es una pantalla).
  if (rule.setupCents > 0) {
    breakdown.push({ code: 'SETUP', ...ruleRef, qty: 1, unitCents: rule.setupCents, amountCents: rule.setupCents });
  }
  if (rule.perInkSetupCents > 0 && inkCount > 0) {
    breakdown.push({
      code: 'SETUP_PER_INK',
      ...ruleRef,
      qty: inkCount,
      unitCents: rule.perInkSetupCents,
      amountCents: rule.perInkSetupCents * inkCount,
      params: { inks: inkCount },
    });
  }

  // 3. Marcaje por unidad, con sus recargos por tinta y por pasada.
  if (rule.unitMarkingCents > 0) {
    breakdown.push({
      code: 'UNIT_MARKING',
      ...ruleRef,
      qty: totalQty,
      unitCents: rule.unitMarkingCents,
      amountCents: rule.unitMarkingCents * totalQty,
      params: { qtyBracket: `${rule.qtyFrom}-${rule.qtyTo ?? '+'}` },
    });
  }
  if (rule.perInkUnitCents > 0 && inkCount > 0) {
    const unit = rule.perInkUnitCents * inkCount;
    breakdown.push({
      code: 'PER_INK_UNIT',
      ...ruleRef,
      qty: totalQty,
      unitCents: unit,
      amountCents: unit * totalQty,
      params: { inks: inkCount },
    });
  }
  if (rule.perPassUnitCents > 0 && input.passes > 0) {
    const unit = rule.perPassUnitCents * input.passes;
    breakdown.push({
      code: 'PER_PASS_UNIT',
      ...ruleRef,
      qty: totalQty,
      unitCents: unit,
      amountCents: unit * totalQty,
      params: { passes: input.passes },
    });
  }

  // 4. Recargos declarados como datos, con su condicion.
  for (const s of rule.surcharges) {
    if (!surchargeApplies(s, { areaMm2, inks: inkCount, darkGarment: input.darkGarment, technicalWhite })) continue;

    if (s.kind === 'fixed') {
      breakdown.push({ code: 'SURCHARGE', ...ruleRef, qty: 1, unitCents: s.value, amountCents: s.value, params: { surcharge: s.code } });
    } else if (s.kind === 'per_unit') {
      breakdown.push({
        code: 'SURCHARGE',
        ...ruleRef,
        qty: totalQty,
        unitCents: s.value,
        amountCents: s.value * totalQty,
        params: { surcharge: s.code },
      });
    } else {
      const base = breakdown.reduce((acc, b) => acc + b.amountCents, 0);
      const amount = Math.round((base * s.value) / 10000);
      breakdown.push({
        code: 'SURCHARGE',
        ...ruleRef,
        qty: 1,
        unitCents: amount,
        amountCents: amount,
        params: { surcharge: s.code, percent: s.value / 100 },
      });
    }
  }

  let subtotalCents = breakdown.reduce((acc, b) => acc + b.amountCents, 0);

  // 5. Minimo de pedido: se declara como ajuste visible, no se esconde.
  if (subtotalCents < input.priceBook.minOrderCents) {
    const diff = input.priceBook.minOrderCents - subtotalCents;
    breakdown.push({
      code: 'MINIMUM_ORDER_ADJUSTMENT',
      ruleId: input.priceBook.id,
      ruleVersion: input.priceBook.version,
      qty: 1,
      unitCents: diff,
      amountCents: diff,
      params: { minimumCents: input.priceBook.minOrderCents },
    });
    subtotalCents = input.priceBook.minOrderCents;
  }

  const vatCents = Math.round((subtotalCents * input.priceBook.vatBasisPoints) / 10000);
  const totalCents = subtotalCents + vatCents;

  const validUntil = new Date(Date.parse(input.now) + input.priceBook.validityHours * 3600_000).toISOString();
  const ruleVersions = [`${rule.id}@${rule.version}`, `${input.priceBook.id}@${input.priceBook.version}`];

  const calculationHash = canonicalHash({
    lines: input.lines,
    techniqueId: input.techniqueId,
    zoneId: input.zoneId,
    passes: input.passes,
    darkGarment: input.darkGarment,
    inkCount,
    areaMm2,
    ruleVersions,
    breakdown,
    subtotalCents,
    vatCents,
    totalCents,
  });

  return {
    breakdown,
    subtotalCents,
    vatCents,
    totalCents,
    totalQty,
    inkCount,
    markedAreaMm2: areaMm2,
    ruleVersions,
    calculationHash,
    validUntil,
  };
}

/** Un presupuesto caducado no se confirma en silencio: se recalcula y se avisa. */
export function isQuoteExpired(quote: Pick<Quote, 'validUntil'>, nowIso: string): boolean {
  return Date.parse(nowIso) > Date.parse(quote.validUntil);
}

/** Centimos -> euros con 2 decimales, para mostrar y para la API. */
export function centsToEuros(cents: number): number {
  return Number((cents / 100).toFixed(2));
}
